import React, { useEffect, useRef, useState, useContext } from "react";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import { useSelector } from "react-redux";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { generateUrlForWorkspace } from "../Workspace/common";
import { useLocation } from "react-router-dom";
import { CountryFlag } from "../Workspace/CountryFlag";
import { LayoutUIContex } from "../Layout/LayoutUIContext";
import "./WorkspaceSelector.css";

const WorkspaceSelector = () => {
    const location = useLocation();
    const { workspaces } = useSelector((state) => state);
    const { collapsed } = useContext(LayoutUIContex);

    const [searchQuery, setSearchQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    const searchInputRef = useRef(null);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (isOpen && collapsed) setIsOpen(false);
    }, [collapsed, isOpen]);

    const focusSearchInput = () => {
        setTimeout(() => searchInputRef.current?.focus(), 0);
    };

    if (!workspaces?.fetched) return null;

    const generateUrl = (workspaceNumber) => generateUrlForWorkspace(workspaceNumber);

    const filtered = (workspaces.data || []).filter((w) =>
        (w.name || "Unnamed Workspace").toLowerCase().includes(searchQuery.toLowerCase())
    );

    const clearSearch = () => {
        setSearchQuery("");
        focusSearchInput();
    };

    const currentName = workspaces.current?.seqNumber
        ? (workspaces.current?.name || "Unnamed workspace").trim()
        : "Select Workspace";

    const isValidCountryCode = (code) => /^[a-zA-Z]{2}$/.test((code || "").trim());

    return (
        <div className="workspace-selector">
            <Dropdown nav inNavbar isOpen={isOpen} toggle={() => setIsOpen((v) => !v)}>
                <DropdownToggle
                    nav
                    className="workspace-toggle"
                    onClick={focusSearchInput}
                    aria-label="Select workspace"
                >
          <span className="workspace-toggle__content">
            <span className="workspace-toggle__flag">
              {isValidCountryCode(workspaces.current?.countryCode) ? (
                  <CountryFlag
                      countryCode={workspaces.current.countryCode}
                      width={25}
                      className="country-flag"
                  />
              ) : (
                  <span className="workspace-toggle__initials">
                      {currentName.replace(/\s+/g, " ").trim().slice(0, 2).toUpperCase()}
                  </span>
              )}
            </span>
            <span className="workspace-toggle__label" title={typeof currentName === "string" ? currentName : ""}>
              {currentName}
            </span>
            {!collapsed && <KeyboardArrowDownIcon className="workspace-toggle__caret" fontSize="small" />}
          </span>
                </DropdownToggle>

                <DropdownMenu className="workspace-dropdown-menu" right={false}>
                    <div className="workspace-search-container">
                        <TextField
                            inputRef={searchInputRef}
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search..."
                            fullWidth
                            InputProps={{
                                notched: false,
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton edge="end" onClick={clearSearch}>
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </div>

                    <div className="workspace-dropdown-list">
                        {filtered.length ? (
                            filtered.map((w) => (
                                <DropdownItem
                                    key={w.seqNumber}
                                    className="workspace-item"
                                    tag="a"
                                    href={`/#${generateUrl(w.seqNumber)}`}
                                >
                                    <div className="workspace-info">
                                        {isValidCountryCode(w.countryCode) ? (
                                            <CountryFlag countryCode={w.countryCode} width={25} className="country-flag" />
                                        ) : (
                                            <span className="workspace-item__initials">
                                                {(w.name || "?").trim().slice(0, 2).toUpperCase()}
                                            </span>
                                        )}
                                        <span className="workspace-link">{(w.name || "Unnamed Workspace").trim()}</span>
                                    </div>
                                </DropdownItem>
                            ))
                        ) : (
                            <DropdownItem className="workspace-item">
                                <span>No workspaces found</span>
                            </DropdownItem>
                        )}
                    </div>
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

export default WorkspaceSelector;
